/**
 * NORATIV ELITE COLLECTION v1.0
 * High-Performance Collection Page JS
 */

const NRC = {
  sectionId: null,
  loading: false,
  qvCurrentImages: [],
  qvCurrentImgIdx: 0,
  qvSelectedVariantId: null,
  _qvProduct: null,
  _toastTimer: null,

  init() {
    const root = document.getElementById('nrc-root');
    if (!root) return;
    this.sectionId = root.dataset.sectionId;

    // Fail-safe init for each component
    const safeExec = (fn) => { try { fn.call(this); } catch(e) { console.warn('NRC: Init error', e); } };

    safeExec(this.bindFilterPills);
    safeExec(this.bindSort);
    safeExec(this.bindStickyControls);
    safeExec(this.bindQuickView);
    safeExec(this.bindLoadMore);
    safeExec(this.bindSwatches);
    safeExec(this.bindStickyBar);
    safeExec(this.bindOBT);
    safeExec(this.bindBundleCTA);
    safeExec(this.bindQuickAddCards); // <-- FIXED: Added this missing activation call
    safeExec(this.initToast);
    safeExec(this.bindSizeGuide);
    
    console.log('NRC: Elite Collection Initialized v2.0');
  },

  // ─── Filter Pills ───────────────────────────────────────────
  bindFilterPills() {
    // Toggle dropdown open/close
    document.querySelectorAll('.nrc-filter-pill[data-filter-group]').forEach(pill => {
      pill.addEventListener('click', e => {
        e.stopPropagation();
        const isOpen = pill.classList.contains('open');
        document.querySelectorAll('.nrc-filter-pill.open').forEach(p => p.classList.remove('open'));
        if (!isOpen) pill.classList.add('open');
      });
    });

    // Close on outside click
    document.addEventListener('click', () => {
      document.querySelectorAll('.nrc-filter-pill.open').forEach(p => p.classList.remove('open'));
    });

    // Prevent dropdown clicks from closing
    document.querySelectorAll('.nrc-filter-dropdown').forEach(dd => {
      dd.addEventListener('click', e => e.stopPropagation());
    });

    // Checkbox changes
    document.addEventListener('change', e => {
      if (e.target.matches('.nrc-filter-option input[type="checkbox"]')) {
        this.applyFilters();
      }
    });

    // Clear all
    document.getElementById('nrc-clear-btn')?.addEventListener('click', () => {
      document.querySelectorAll('.nrc-filter-option input[type="checkbox"]').forEach(cb => { cb.checked = false; });
      document.querySelectorAll('.nrc-filter-pill[data-filter-group]').forEach(p => p.classList.remove('active'));
      this.applyFilters();
    });

    // Active badge removal (delegated)
    document.getElementById('nrc-active-filters')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-remove-filter]');
      if (!btn) return;
      const { filterParam, filterValue } = btn.dataset;
      const cb = document.querySelector(`.nrc-filter-option input[data-param="${filterParam}"][data-value="${filterValue}"]`);
      if (cb) { cb.checked = false; }
      this.applyFilters();
    });
  },

  buildFilterURL() {
    const url = new URL(window.location.href);
    // Clear existing filter params
    [...url.searchParams.keys()].filter(k => k.startsWith('filter.')).forEach(k => url.searchParams.delete(k));

    document.querySelectorAll('.nrc-filter-option input[type="checkbox"]:checked').forEach(cb => {
      const param = cb.dataset.param;
      const value = cb.dataset.value;
      url.searchParams.append(param, value);
    });

    const sortEl = document.getElementById('nrc-sort');
    if (sortEl && sortEl.value) url.searchParams.set('sort_by', sortEl.value);

    return url;
  },

  updateActiveFilterBadges(url) {
    const container = document.getElementById('nrc-active-filters');
    const clearBtn = document.getElementById('nrc-clear-btn');
    if (!container) return;

    const params = [];
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) params.push({ key, value });
    });

    if (params.length === 0) {
      container.innerHTML = '';
      container.classList.remove('visible');
      clearBtn?.classList.remove('visible');
      // Update pill active states
      document.querySelectorAll('.nrc-filter-pill[data-filter-group]').forEach(p => p.classList.remove('active'));
      return;
    }

    container.classList.add('visible');
    clearBtn?.classList.add('visible');
    container.innerHTML = params.map(({ key, value }) => `
      <span class="nrc-active-badge">
        ${value}
        <button data-remove-filter data-filter-param="${key}" data-filter-value="${value}" aria-label="Remove filter">✕</button>
      </span>
    `).join('');

    // Mark filter pill groups as active
    document.querySelectorAll('.nrc-filter-pill[data-filter-group]').forEach(pill => {
      const group = pill.dataset.filterGroup;
      const hasActive = [...url.searchParams.keys()].some(k => k.includes(group));
      pill.classList.toggle('active', hasActive);
    });
  },

  async applyFilters() {
    const url = this.buildFilterURL();
    this.updateActiveFilterBadges(url);
    history.pushState({}, '', url.toString());
    await this.fetchGrid(url.toString());
  },

  // ─── Sort ────────────────────────────────────────────────────
  bindSort() {
    document.getElementById('nrc-sort')?.addEventListener('change', () => this.applyFilters());
  },

  // ─── AJAX Grid Fetch ─────────────────────────────────────────
  async fetchGrid(url) {
    const section = document.getElementById('nrc-products-section');
    if (!section || this.loading) return;
    this.loading = true;
    section.classList.add('loading');

    try {
      const fetchUrl = url.includes('?')
        ? `${url}&section_id=${this.sectionId}`
        : `${url}?section_id=${this.sectionId}`;

      const res = await fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newSection = doc.getElementById('nrc-products-section');
      if (newSection) {
        section.innerHTML = newSection.innerHTML;
        // Re-bind events for new cards
        this.bindSwatches();
        this.bindQuickViewCards();
        this.bindLoadMore();
        this.bindQuickAddCards();
      }
    } catch (err) {
      console.error('NRC: Fetch error', err);
    } finally {
      this.loading = false;
      section.classList.remove('loading');
    }
  },

  // ─── Sticky Controls ─────────────────────────────────────────
  bindStickyControls() {
    const controls = document.getElementById('nrc-controls');
    if (!controls) return;
    const observer = new IntersectionObserver(
      ([e]) => controls.classList.toggle('is-stuck', e.intersectionRatio < 1),
      { threshold: [1] }
    );
    observer.observe(controls);
  },

  // ─── Color Swatches ──────────────────────────────────────────
  bindSwatches() {
    document.querySelectorAll('.nrc-swatch:not([data-bound])').forEach(swatch => {
      swatch.dataset.bound = '1';
      swatch.addEventListener('mouseenter', () => {
        const card = swatch.closest('.nrc-card');
        if (!card) return;
        const img = card.querySelector('.nrc-card-img-alt');
        const altSrc = swatch.dataset.image;
        if (img && altSrc) img.src = altSrc;
      });
      swatch.addEventListener('click', () => {
        const card = swatch.closest('.nrc-card');
        if (!card) return;
        card.querySelectorAll('.nrc-swatch').forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
        const img = card.querySelector('.nrc-card-img');
        const imgAlt = card.querySelector('.nrc-card-img-alt');
        const mainSrc = swatch.dataset.image;
        if (img && mainSrc) img.src = mainSrc;
        if (imgAlt && mainSrc) imgAlt.src = mainSrc;
      });
    });
  },

  // ─── Quick View ──────────────────────────────────────────────
  bindQuickView() {
    this.bindQuickViewCards();

    // Close buttons
    document.getElementById('nrc-qv-overlay')?.addEventListener('click', () => this.closeQV());
    document.getElementById('nrc-qv-close')?.addEventListener('click', () => this.closeQV());

    // Keyboard
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closeQV();
    });

    // Option buttons (delegated)
    document.getElementById('nrc-qv-modal')?.addEventListener('click', e => {
      const btn = e.target.closest('.nrc-qv-opt-btn:not(.unavail)');
      if (!btn) return;
      const group = btn.closest('.nrc-qv-option-group');
      group?.querySelectorAll('.nrc-qv-opt-btn').forEach(b => b.classList.remove('selected'));
      btn.classList.add('selected');
      this.updateQVVariant();
    });

    // Gallery dots
    document.getElementById('nrc-qv-modal')?.addEventListener('click', e => {
      const dot = e.target.closest('.nrc-qv-thumb');
      if (!dot) return;
      this.qvGoto(parseInt(dot.dataset.idx));
    });

    // ATC
    document.getElementById('nrc-qv-atc')?.addEventListener('click', () => this.qvAddToCart());
  },

  bindQuickViewCards() {
    document.querySelectorAll('.nrc-qv-trigger:not([data-bound])').forEach(btn => {
      btn.dataset.bound = '1';
      btn.addEventListener('click', e => {
        e.preventDefault();
        const handle = btn.dataset.productHandle;
        if (handle) this.openQV(handle);
      });
    });
  },

  async openQV(handle) {
    const overlay = document.getElementById('nrc-qv-overlay');
    const modal   = document.getElementById('nrc-qv-modal');
    const info    = document.getElementById('nrc-qv-info');
    if (!modal) return;

    // Show loading state
    overlay?.classList.add('open');
    modal.classList.add('open');
    if (info) info.innerHTML = '<div style="padding:32px;text-align:center;color:#aaa;">Loading…</div>';

    try {
      const res = await fetch(`/products/${handle}.js`);
      const p = await res.json();
      this.renderQV(p);
    } catch (err) {
      console.error('NRC: QV error', err);
      if (info) info.innerHTML = '<div style="padding:32px;">Failed to load. <a href="/products/' + handle + '">View product</a></div>';
    }
  },

  renderQV(p) {
    const gallery = document.getElementById('nrc-qv-gallery-img');
    const thumbs  = document.getElementById('nrc-qv-thumbs');
    const info    = document.getElementById('nrc-qv-info');
    const viewFull = document.getElementById('nrc-qv-view-full');

    // Images
    this.qvCurrentImages = p.images.slice(0, 6);
    this.qvCurrentImgIdx = 0;
    if (thumbs) {
      thumbs.innerHTML = this.qvCurrentImages.map((_, i) =>
        `<button class="nrc-qv-dot ${i === 0 ? 'active' : ''}" data-idx="${i}" aria-label="Image ${i+1}"></button>`
      ).join('');
    }
    const galleryContainer = document.getElementById('nrc-qv-gallery');
    if (galleryContainer) {
      galleryContainer.innerHTML = `
        <div class="nrc-qv-slides" id="nrc-qv-slides">
          ${this.qvCurrentImages.map(img => `<div class="nrc-qv-slide"><img src="${img}" loading="lazy" /></div>`).join('')}
        </div>
        <div class="nrc-qv-dots" id="nrc-qv-thumbs">
          ${this.qvCurrentImages.map((_, i) => `<button class="nrc-qv-dot ${i === 0 ? 'active' : ''}" data-idx="${i}"></button>`).join('')}
        </div>
      `;
      const slides = document.getElementById('nrc-qv-slides');
      if (slides) slides.scrollLeft = 0; // Force start at first image
      this.bindQVScroll();
    }
    if (viewFull) viewFull.href = `/products/${p.handle}`;

    if (!info) return;
    const variant = p.variants[0];
    this.qvSelectedVariantId = variant?.id;

    const price = this.formatMoney(variant?.price ?? p.price);
    const compare = variant?.compare_at_price > variant?.price
      ? this.formatMoney(variant.compare_at_price) : null;
    const savePct = compare
      ? Math.round((1 - variant.price / variant.compare_at_price) * 100) : 0;

    // Build options HTML
    let optionsHtml = '';
    p.options.forEach((opt, idx) => {
      const vals = [...new Set(p.variants.map(v => v.options[idx]))];
      optionsHtml += `
        <div class="nrc-qv-option-group" data-option-idx="${idx}">
          <div class="nrc-qv-option-label">${opt.name || opt}</div>
          <div class="nrc-qv-option-btns">
            ${vals.map(val => {
              const avail = p.variants.some(v => v.options[idx] === val && v.available);
              return `<button class="nrc-qv-opt-btn ${idx === 0 ? 'selected' : ''} ${!avail ? 'unavail' : ''}" data-option-val="${val}">${val}</button>`;
            }).join('')}
          </div>
        </div>`;
    });

    info.innerHTML = `
      <h2 class="nrc-qv-title">${p.title}</h2>
      <div class="nrc-qv-price-row">
        <span class="nrc-qv-price">${price}</span>
        ${compare ? `<span class="nrc-qv-compare">${compare}</span>` : ''}
        ${savePct > 0 ? `<span class="nrc-qv-save">Save ${savePct}%</span>` : ''}
      </div>
      ${optionsHtml}
      <button class="nrc-qv-atc" id="nrc-qv-atc">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
        Add to Cart
      </button>
      <a class="nrc-qv-view-full" href="/products/${p.handle}" id="nrc-qv-view-full">View full details →</a>
    `;
    this._qvProduct = p;
    this.updateQVVariant();

    // Re-bind ATC
    document.getElementById('nrc-qv-atc')?.addEventListener('click', () => this.qvAddToCart());

    // Re-bind Dots (click to scroll)
    document.querySelectorAll('.nrc-qv-dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.idx);
        const slides = document.getElementById('nrc-qv-slides');
        if (slides) {
          const slideWidth = slides.offsetWidth;
          slides.scrollTo({ left: slideWidth * idx, behavior: 'smooth' });
        }
      });
    });
  },

  bindQVScroll() {
    const slides = document.getElementById('nrc-qv-slides');
    const dots = document.querySelectorAll('.nrc-qv-dot');
    if (!slides || !dots.length) return;

    slides.addEventListener('scroll', () => {
      const idx = Math.round(slides.scrollLeft / slides.offsetWidth);
      dots.forEach((d, i) => d.classList.toggle('active', i === idx));
    }, { passive: true });
  },

  updateQVVariant() {
    const p = this._qvProduct;
    if (!p) return;
    const selected = [];
    document.querySelectorAll('#nrc-qv-info .nrc-qv-option-group').forEach(group => {
      const sel = group.querySelector('.nrc-qv-opt-btn.selected');
      if (sel) selected.push(sel.dataset.optionVal);
    });
    const variant = p.variants.find(v =>
      selected.every((val, i) => v.options[i] === val)
    ) || p.variants[0];
    this.qvSelectedVariantId = variant?.id;
    // Update price
    const priceEl = document.querySelector('.nrc-qv-price');
    if (priceEl && variant) priceEl.textContent = this.formatMoney(variant.price);
    // Update image
    if (variant?.featured_image?.src) {
      const img = document.getElementById('nrc-qv-gallery-img');
      if (img) img.src = variant.featured_image.src;
    }
  },

  qvGoto(idx) {
    this.qvCurrentImgIdx = idx;
    const img = document.getElementById('nrc-qv-gallery-img');
    if (img && this.qvCurrentImages[idx]) img.src = this.qvCurrentImages[idx];
    document.querySelectorAll('.nrc-qv-thumb').forEach((d, i) => d.classList.toggle('active', i === idx));
  },

  closeQV() {
    document.getElementById('nrc-qv-overlay')?.classList.remove('open');
    document.getElementById('nrc-qv-modal')?.classList.remove('open');
    this._qvProduct = null;
  },

  async qvAddToCart() {
    const btn = document.getElementById('nrc-qv-atc');
    if (!this.qvSelectedVariantId || !btn) return;
    btn.textContent = 'Adding…';
    btn.style.opacity = '0.7';
    await this.addToCart(this.qvSelectedVariantId, 1);
    btn.textContent = '✓ Added!';
    setTimeout(() => {
      btn.textContent = 'Add to Cart';
      btn.style.opacity = '';
    }, 2000);
  },

  // ─── Quick Add (from card) ───────────────────────────────────
  bindQuickAddCards() {
    document.querySelectorAll('.nrc-card-qa:not([data-bound])').forEach(btn => {
      btn.dataset.bound = '1';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.dataset.variantId;
        const name = btn.dataset.productTitle;
        const hasOptions = btn.dataset.hasOptions === 'true';
        const handle = btn.dataset.productHandle;

        if (hasOptions && handle) {
          NRC.openQV(handle);
          return;
        }

        if (!variantId) return;
        btn.textContent = 'Adding…';
        await NRC.addToCart(variantId, 1);
        NRC.showToast(`${name} added to cart`);
        setTimeout(() => {
          btn.textContent = '+ Quick Add';
        }, 2000);
      });
    });
  },

  // ─── Cart API helpers ────────────────────────────────────────
  async addToCart(variantId, qty = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
      });
      const data = await res.json();
      this.updateCartCount();
      return data;
    } catch (err) {
      console.error('NRC: add to cart error', err);
    }
  },

  async updateCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-nrc-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
      // Also sync with theme header
      document.querySelectorAll('.ngh-cart-count, .cart-count-bubble span, [data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (_) {}
  },

  // ─── Load More ───────────────────────────────────────────────
  bindLoadMore() {
    const btn = document.getElementById('nrc-load-more');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const nextUrl = btn.dataset.url;
      if (!nextUrl || this.loading) return;
      btn.classList.add('loading');
      this.loading = true;
      try {
        const res = await fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const newCards = doc.querySelectorAll('.nrc-card');
        const grid = document.getElementById('nrc-grid');
        newCards.forEach(card => {
          card.style.opacity = '0';
          card.style.transform = 'translateY(16px)';
          grid?.appendChild(card);
          requestAnimationFrame(() => {
            card.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
            card.style.opacity = '1';
            card.style.transform = 'none';
          });
        });
        this.bindSwatches();
        this.bindQuickViewCards();
        this.bindQuickAddCards();
        // Update next URL
        const newLoadMore = doc.getElementById('nrc-load-more');
        if (newLoadMore) {
          btn.dataset.url = newLoadMore.dataset.url;
        } else {
          btn.closest('.nrc-load-more-wrap')?.remove();
        }
      } catch (err) {
        console.error('NRC: load more error', err);
      } finally {
        this.loading = false;
        btn.classList.remove('loading');
      }
    });
  },

  // ─── Sticky bar ──────────────────────────────────────────────
  bindStickyBar() {
    const bar = document.getElementById('nrc-sticky-bar');
    if (!bar) return;
    // Show after scrolling 40% of the page
    window.addEventListener('scroll', () => {
      const scrolled = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      bar.classList.toggle('visible', scrolled > 0.35);
    }, { passive: true });
    document.getElementById('nrc-sticky-dismiss')?.addEventListener('click', () => {
      bar.remove();
    });
  },

  // ─── Often Bought Together ───────────────────────────────────
  bindOBT() {
    const btn = document.getElementById('nrc-obt-add-all');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const ids = btn.dataset.variantIds?.split(',').filter(Boolean);
      if (!ids?.length) return;
      btn.textContent = 'Adding…';
      btn.style.opacity = '0.7';
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: ids.map(id => ({ id: parseInt(id), quantity: 1 })) })
        });
        await this.updateCartCount();
        btn.textContent = '✓ All Added to Cart!';
        this.showToast('Bundle added to cart');
      } catch (err) {
        btn.textContent = 'Add All to Cart';
        btn.style.opacity = '';
      }
    });
  },

  // ─── Bundle CTA ──────────────────────────────────────────────
  bindBundleCTA() {
    document.querySelectorAll('.nrc-bundle-cta').forEach(btn => {
      btn.addEventListener('click', () => {
        const collectionUrl = btn.dataset.url || window.location.pathname;
        window.location.href = collectionUrl;
      });
    });
  },

  // ─── Toast ───────────────────────────────────────────────────
  initToast() {
    if (!document.getElementById('nrc-toast')) {
      const toast = document.createElement('div');
      toast.id = 'nrc-toast';
      toast.className = 'nrc-toast';
      toast.innerHTML = `
        <div class="nrc-toast-check">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <span id="nrc-toast-msg"></span>
      `;
      document.body.appendChild(toast);
    }
  },

  showToast(msg) {
    const toast = document.getElementById('nrc-toast');
    const msgEl = document.getElementById('nrc-toast-msg');
    if (!toast || !msgEl) return;
    msgEl.textContent = msg;
    toast.classList.add('show');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => toast.classList.remove('show'), 3000);
  },

  // ─── Size Guide ──────────────────────────────────────────────
  bindSizeGuide() {
    const openBtn = document.getElementById('nrc-size-guide-open');
    const closeBtn = document.getElementById('nrc-sg-close');
    const overlay = document.getElementById('nrc-sg-overlay');
    const modal = document.getElementById('nrc-sg-modal');

    if (!openBtn || !modal) return;

    openBtn.addEventListener('click', () => {
      overlay?.classList.add('open');
      modal?.classList.add('open');
      document.body.style.overflow = 'hidden';
    });

    const closeSG = () => {
      overlay?.classList.remove('open');
      modal?.classList.remove('open');
      document.body.style.overflow = '';
    };

    closeBtn?.addEventListener('click', closeSG);
    overlay?.addEventListener('click', closeSG);
  },

  // ─── Utilities ───────────────────────────────────────────────
  formatMoney(cents) {
    if (!cents && cents !== 0) return '';
    const amount = (cents / 100).toFixed(2);
    return `€${amount.replace('.', ',')}`;
  }
};

document.addEventListener('DOMContentLoaded', () => NRC.init());

// Expose for fail-safe inline calls and theme integration
window.NRC = NRC;
window.nrcOpenQV = handle => NRC.openQV(handle);
window.nrcCloseQV = () => NRC.closeQV();
